import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig } from "@intelligems/ig-types";
import { hexToRgbA } from "@intelligems/ig-utils";
import { Box, Tooltip, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { AnalyticsLegendIcon } from "@src/images/Icons/AnalyticsLegendIcon";
import { selectedVariations } from "@src/store/slices/variations";
import { analyticsTooltipTimeConverter } from "@src/utils/dateConverters";

export const TimeSeriesTooltip = ({
  active,
  payload,
  label,
  config,
  colorMap,
  graphInterval,
}: TooltipProps<ValueType, NameType> & {
  config: GraphConfig;
  graphInterval?: string;
  colorMap?: { [key: string]: string };
}) => {
  const theme = useTheme();
  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const formattedLabel = useMemo(() => {
    if (!label) {
      return "";
    }

    if (graphInterval === "WEEK") {
      return `Week of ${label}`;
    }
    return label;
  }, [label, graphInterval]);

  if (!payload || !active) {
    return null;
  }

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          padding: "10px !important",
          borderRadius: "5px",
          backgroundColor: hexToRgbA(theme.igPrimitives.gray["A400"], 0.92),
          minWidth: "220px",
        }}
      >
        <Grid container direction={"column"} rowSpacing={1.2}>
          <Grid>
            <IgTypography
              color={"dark"}
              sx={{
                fontWeight: 700,
              }}
            >
              {formattedLabel}
            </IgTypography>
          </Grid>
          {payload
            .filter(
              (pay) => !((pay.dataKey as string) || "").includes("interval")
            )
            .map((pay, index) => (
              <Grid
                columnSpacing={1.2}
                container
                direction={"row"}
                key={index}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Grid
                  container
                  direction={"row"}
                  alignItems={"center"}
                  columnSpacing={1.2}
                  wrap={"nowrap"}
                  size={7}
                >
                  <Grid>
                    <AnalyticsLegendIcon
                      sx={{ width: "10px", height: "10px" }}
                      fill={
                        colorMap && pay.dataKey
                          ? colorMap[pay.dataKey]
                          : TimeSeriesColors[index]
                      }
                    />
                  </Grid>
                  <Grid
                    sx={{
                      width: "110px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    <IgTypography
                      color={"dark"}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "110px",
                      }}
                    >
                      {
                        variations.find(
                          (variation) => variation.id === pay.dataKey
                        )?.name
                      }
                    </IgTypography>
                  </Grid>
                </Grid>
                <Grid
                  columnSpacing={1}
                  container
                  direction={"row"}
                  alignItems={"center"}
                  wrap={"nowrap"}
                  size={5}
                >
                  <Grid>
                    <IgTypography
                      color={"dark"}
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      {graphValueFormatter({
                        isAxisTick: false,
                        value: pay.payload[pay.dataKey!],
                        configDataTypes: config.dataTypes,
                      })}
                    </IgTypography>
                  </Grid>
                  {/*{pay.payload[`${pay.dataKey}-range`] ? (*/}
                  {/*  <Grid>*/}
                  {/*    <IgTypography variant={"body2"} color={"dark"}>*/}
                  {/*      &plusmn;{" "}*/}
                  {/*      {timeSeriesTooltipPlusMinusFormatter(*/}
                  {/*        pay.payload[`${pay.dataKey}-range`]*/}
                  {/*      )}*/}
                  {/*    </IgTypography>*/}
                  {/*  </Grid>*/}
                  {/*) : null}*/}
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  }

  return null;
};

export const ReferenceLineTooltip = ({
  props,
  experienceAction,
  timezone,
  locale,
}) => {
  const { x, y } = props.viewBox;
  const d = 20;
  const r = d / 2;
  const theme = useTheme();
  const transform =
    experienceAction.action === "pause"
      ? `translate(${x - r} ${y - d + 10})`
      : `translate(${x - r - 1} ${y - d + 8})`;

  if (experienceAction.action === "pause") {
    return (
      <Tooltip
        title={
          <IgTypography color={"dark"} variant={"body2"}>
            Test paused{" "}
            {analyticsTooltipTimeConverter(
              experienceAction.actionTs,
              locale,
              timezone
            )}
            <br />
            <br />
            Data while paused not included in analytics
          </IgTypography>
        }
      >
        <g transform={transform}>
          <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.2207 14.4175H9.2207V6.41748H7.2207V14.4175ZM11.2207 14.4175H13.2207V6.41748H11.2207V14.4175ZM10.2207 20.4175C8.83737 20.4175 7.53737 20.155 6.3207 19.63C5.10404 19.105 4.0457 18.3925 3.1457 17.4925C2.2457 16.5925 1.5332 15.5341 1.0082 14.3175C0.483203 13.1008 0.220703 11.8008 0.220703 10.4175C0.220703 9.03415 0.483203 7.73415 1.0082 6.51748C1.5332 5.30081 2.2457 4.24248 3.1457 3.34248C4.0457 2.44248 5.10404 1.72998 6.3207 1.20498C7.53737 0.67998 8.83737 0.41748 10.2207 0.41748C11.604 0.41748 12.904 0.67998 14.1207 1.20498C15.3374 1.72998 16.3957 2.44248 17.2957 3.34248C18.1957 4.24248 18.9082 5.30081 19.4332 6.51748C19.9582 7.73415 20.2207 9.03415 20.2207 10.4175C20.2207 11.8008 19.9582 13.1008 19.4332 14.3175C18.9082 15.5341 18.1957 16.5925 17.2957 17.4925C16.3957 18.3925 15.3374 19.105 14.1207 19.63C12.904 20.155 11.604 20.4175 10.2207 20.4175Z"
              fill="#BBBBBB"
            />
            <path
              d="M7.2082 14.4175V14.43H7.2207H9.2207H9.2332V14.4175V6.41748V6.40498H9.2207H7.2207H7.2082V6.41748V14.4175ZM11.2082 14.4175V14.43H11.2207H13.2207H13.2332V14.4175V6.41748V6.40498H13.2207H11.2207H11.2082V6.41748V14.4175ZM10.2207 20.405C8.839 20.405 7.54069 20.1428 6.32566 19.6185C5.11038 19.0941 4.05338 18.3825 3.15454 17.4836C2.2557 16.5848 1.54408 15.5278 1.01968 14.3125C0.495385 13.0975 0.233203 11.7992 0.233203 10.4175C0.233203 9.03577 0.495385 7.73747 1.01968 6.52243C1.54408 5.30716 2.2557 4.25016 3.15454 3.35132C4.05338 2.45248 5.11038 1.74086 6.32566 1.21646C7.54069 0.692162 8.839 0.42998 10.2207 0.42998C11.6024 0.42998 12.9007 0.692162 14.1158 1.21646C15.331 1.74086 16.388 2.45248 17.2869 3.35132C18.1857 4.25016 18.8973 5.30716 19.4217 6.52243C19.946 7.73747 20.2082 9.03577 20.2082 10.4175C20.2082 11.7992 19.946 13.0975 19.4217 14.3125C18.8973 15.5278 18.1857 16.5848 17.2869 17.4836C16.388 18.3825 15.331 19.0941 14.1158 19.6185C12.9007 20.1428 11.6024 20.405 10.2207 20.405Z"
              stroke="black"
              strokeOpacity="0.23"
              strokeWidth="0.025"
            />
          </svg>
        </g>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip
        title={`Test resumed ${analyticsTooltipTimeConverter(
          experienceAction.actionTs,
          locale,
          timezone
        )}`}
      >
        <g transform={transform}>
          <svg
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_59115_42615"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="25"
              height="25"
            >
              <rect
                x="0.220703"
                y="0.41748"
                width="24"
                height="24"
                fill="#D9D9D9"
              />
              <rect
                x="0.720703"
                y="0.91748"
                width="23"
                height="23"
                stroke="black"
                strokeOpacity="0.23"
              />
            </mask>
            <g mask="url(#mask0_59115_42615)">
              <path
                d="M9.7207 16.9175L16.7207 12.4175L9.7207 7.91748V16.9175ZM12.2207 22.4175C10.8374 22.4175 9.53737 22.155 8.3207 21.63C7.10404 21.105 6.0457 20.3925 5.1457 19.4925C4.2457 18.5925 3.5332 17.5341 3.0082 16.3175C2.4832 15.1008 2.2207 13.8008 2.2207 12.4175C2.2207 11.0341 2.4832 9.73415 3.0082 8.51748C3.5332 7.30081 4.2457 6.24248 5.1457 5.34248C6.0457 4.44248 7.10404 3.72998 8.3207 3.20498C9.53737 2.67998 10.8374 2.41748 12.2207 2.41748C13.604 2.41748 14.904 2.67998 16.1207 3.20498C17.3374 3.72998 18.3957 4.44248 19.2957 5.34248C20.1957 6.24248 20.9082 7.30081 21.4332 8.51748C21.9582 9.73415 22.2207 11.0341 22.2207 12.4175C22.2207 13.8008 21.9582 15.1008 21.4332 16.3175C20.9082 17.5341 20.1957 18.5925 19.2957 19.4925C18.3957 20.3925 17.3374 21.105 16.1207 21.63C14.904 22.155 13.604 22.4175 12.2207 22.4175Z"
                fill="#BBBBBB"
              />
              <path
                d="M9.7082 16.9175V16.9404L9.72746 16.928L16.7275 12.428L16.7438 12.4175L16.7275 12.407L9.72746 7.90697L9.7082 7.89458V7.91748V16.9175ZM12.2207 22.405C10.839 22.405 9.54069 22.1428 8.32566 21.6185C7.11038 21.0941 6.05338 20.3825 5.15454 19.4836C4.2557 18.5848 3.54408 17.5278 3.01968 16.3125C2.49538 15.0975 2.2332 13.7992 2.2332 12.4175C2.2332 11.0358 2.49538 9.73747 3.01968 8.52243C3.54408 7.30716 4.2557 6.25016 5.15454 5.35132C6.05338 4.45248 7.11038 3.74086 8.32566 3.21646C9.54069 2.69216 10.839 2.42998 12.2207 2.42998C13.6024 2.42998 14.9007 2.69216 16.1158 3.21646C17.331 3.74086 18.388 4.45248 19.2869 5.35132C20.1857 6.25016 20.8973 7.30716 21.4217 8.52243C21.946 9.73747 22.2082 11.0358 22.2082 12.4175C22.2082 13.7992 21.946 15.0975 21.4217 16.3125C20.8973 17.5278 20.1857 18.5848 19.2869 19.4836C18.388 20.3825 17.331 21.0941 16.1158 21.6185C14.9007 22.1428 13.6024 22.405 12.2207 22.405Z"
                stroke="black"
                strokeOpacity="0.23"
                strokeWidth="0.025"
              />
            </g>
          </svg>
        </g>
      </Tooltip>
    );
  }
};
